import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { NearbyStopsResponse, StopMapItem } from '@mbus/shared';
import { fetchMapStops, fetchMapVehicles, fetchNearby, type VehicleMapItem } from './api';
import { MapView, type MapBounds } from './components/MapView';
import { SearchBar } from './components/SearchBar';
import { StopSheet } from './components/StopSheet';
import { TripSheet } from './components/TripSheet';
import { RtBanner } from './components/RtBanner';

/** 画面状態: 地図のみ / 停留所の発車一覧 / 便詳細 */
type View =
  | { mode: 'map' }
  | { mode: 'stop'; stopUid: string; stopName: string; exact?: boolean }
  | { mode: 'trip'; tripUid: string; serviceDate: string; backStopUid?: string; backStopName?: string; backExact?: boolean };

/** 東京駅 — 位置情報が取れない場合の初期表示 */
const FALLBACK_CENTER = { lat: 35.681236, lon: 139.767125 };

const VEHICLE_POLL_MS = 10_000;
const NEARBY_REFRESH_MS = 30_000;

export function App() {
  const [view, setView] = useState<View>({ mode: 'map' });
  const [userLoc, setUserLoc] = useState<{ lat: number; lon: number } | null>(null);
  const [center, setCenter] = useState(FALLBACK_CENTER);
  const [flyTo, setFlyTo] = useState<{ lat: number; lon: number; zoom?: number } | null>(null);
  const [nearby, setNearby] = useState<NearbyStopsResponse | null>(null);
  const [mapStops, setMapStops] = useState<StopMapItem[]>([]);
  const [vehicles, setVehicles] = useState<VehicleMapItem[]>([]);
  const [geoDenied, setGeoDenied] = useState(false);
  const [tripShape, setTripShape] = useState<[number, number][] | null>(null);

  const boundsRef = useRef<MapBounds | null>(null);
  const zoomRef = useRef(15);

  // ---- 現在地の取得 ---------------------------------------------------------
  useEffect(() => {
    if (!('geolocation' in navigator)) {
      setGeoDenied(true);
      return;
    }
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lon: pos.coords.longitude };
        setUserLoc((prev) => {
          // 初回だけ地図を現在地へ移動
          if (!prev) {
            setFlyTo({ ...loc, zoom: 16 });
            setCenter(loc);
          }
          return loc;
        });
      },
      () => setGeoDenied(true),
      { enableHighAccuracy: true, maximumAge: 15_000, timeout: 10_000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  // ---- 周辺停留所 (中心が動くたび + 定期更新) --------------------------------
  const refreshNearby = useCallback(async (lat: number, lon: number) => {
    try {
      const res = await fetchNearby(lat, lon, 900);
      setNearby(res);
    } catch (err) {
      console.warn('nearby fetch failed', err);
    }
  }, []);

  useEffect(() => {
    void refreshNearby(center.lat, center.lon);
    const t = setInterval(() => void refreshNearby(center.lat, center.lon), NEARBY_REFRESH_MS);
    return () => clearInterval(t);
  }, [center.lat, center.lon, refreshNearby]);

  // ---- 地図移動時: 表示範囲の停留所 + 車両 ----------------------------------
  const handleMoveEnd = useCallback((b: MapBounds, c: { lat: number; lon: number }, zoom: number) => {
    boundsRef.current = b;
    zoomRef.current = zoom;
    setCenter((prev) =>
      Math.abs(prev.lat - c.lat) + Math.abs(prev.lon - c.lon) > 0.0008 ? c : prev,
    );
    if (zoom >= 14 && b.maxLat - b.minLat <= 0.5 && b.maxLon - b.minLon <= 0.5) {
      fetchMapStops(b)
        .then((res) => {
          if (!res.tooWide) setMapStops(res.stops);
        })
        .catch(() => undefined);
    } else {
      setMapStops([]);
    }
  }, []);

  // ---- 車両ポーリング --------------------------------------------------------
  useEffect(() => {
    let stopped = false;
    const poll = async () => {
      const b = boundsRef.current;
      if (!b || zoomRef.current < 12) {
        setVehicles([]);
        return;
      }
      try {
        const res = await fetchMapVehicles(b);
        if (!stopped && !res.tooWide) setVehicles(res.vehicles);
      } catch {
        /* 一時的な失敗は無視して次のポーリングに任せる */
      }
    };
    void poll();
    const t = setInterval(() => void poll(), VEHICLE_POLL_MS);
    return () => {
      stopped = true;
      clearInterval(t);
    };
  }, []);

  // ---- 地図に描く停留所 = 周辺リスト ∪ 表示範囲 ------------------------------
  const stopsForMap = useMemo(() => {
    const byUid = new Map<string, StopMapItem>();
    for (const s of mapStops) byUid.set(s.stopUid, s);
    for (const s of nearby?.stops ?? []) byUid.set(s.stopUid, s); // nearby 優先 (next 情報が新しい)
    return [...byUid.values()];
  }, [mapStops, nearby]);

  const selectedStopUid = view.mode === 'stop' ? view.stopUid : null;

  const openStop = useCallback((stopUid: string, stopName: string, exact?: boolean) => {
    setTripShape(null);
    setView({ mode: 'stop', stopUid, stopName, exact });
  }, []);

  const openTrip = useCallback(
    (tripUid: string, serviceDate: string, back?: { stopUid: string; stopName: string; exact?: boolean }) => {
      setView({
        mode: 'trip',
        tripUid,
        serviceDate,
        backStopUid: back?.stopUid,
        backStopName: back?.stopName,
        backExact: back?.exact,
      });
    },
    [],
  );

  const closeSheet = useCallback(() => {
    setTripShape(null);
    setView({ mode: 'map' });
  }, []);

  return (
    <div className="app">
      <MapView
        stops={stopsForMap}
        vehicles={vehicles}
        userLoc={userLoc}
        flyTo={flyTo}
        selectedStopUid={selectedStopUid}
        tripShape={tripShape}
        onSelectStop={(uid, name) => openStop(uid, name, true)}
        onMoveEnd={handleMoveEnd}
      />

      <div className="top-ui">
        <SearchBar
          onPick={(r) => {
            setFlyTo({ lat: r.lat, lon: r.lon, zoom: 16 });
            setCenter({ lat: r.lat, lon: r.lon });
            if (r.stopUid) openStop(r.stopUid, r.title);
          }}
        />
        {nearby && <RtBanner coverage={nearby.rtCoverage} />}
        {geoDenied && !userLoc && (
          <div className="banner banner-info">
            位置情報が使えないため東京駅周辺を表示しています。検索から場所を指定できます。
          </div>
        )}
      </div>

      {userLoc && (
        <button
          className="locate-btn"
          aria-label="現在地へ移動"
          onClick={() => setFlyTo({ ...userLoc, zoom: 16 })}
        >
          ◎
        </button>
      )}

      {view.mode === 'stop' && (
        <StopSheet
          stopUid={view.stopUid}
          stopName={view.stopName}
          exact={view.exact}
          userLoc={userLoc}
          walkMinutes={nearby?.walkMinutes[view.stopUid] ?? null}
          onClose={closeSheet}
          onSelectTrip={(tripUid, serviceDate) =>
            openTrip(tripUid, serviceDate, {
              stopUid: view.stopUid,
              stopName: view.stopName,
              exact: view.exact,
            })
          }
        />
      )}

      {view.mode === 'trip' && (
        <TripSheet
          tripUid={view.tripUid}
          serviceDate={view.serviceDate}
          onShape={setTripShape}
          onClose={() => {
            if (view.backStopUid && view.backStopName) {
              openStop(view.backStopUid, view.backStopName, view.backExact);
            } else {
              closeSheet();
            }
          }}
        />
      )}
    </div>
  );
}
